import { OrderBookMetrics } from "@cede/types";
import { API_ENDPOINT, Logger, isDexId, isEvmBasedExchange } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useQuery } from "@tanstack/react-query";

const ORDERBOOK_REFRESH_INTERVAL = 5_000; // Refresh orderbook every 5 seconds

type UseOrderBookParams = {
  exchangeId?: string;
  pairSymbol?: string;
  enabled?: boolean;
  refreshInterval?: number;
};

/**
 * Hook to fetch orderbook metrics for a trading pair
 * Note: Only works for CEX exchanges - DEXs use liquidity pools instead of orderbooks
 */
export const useOrderBook = ({
  exchangeId,
  pairSymbol,
  enabled = true,
  refreshInterval = ORDERBOOK_REFRESH_INTERVAL,
}: UseOrderBookParams) => {
  const apiHelper = useApi();

  // Skip for DEX and EVM-based exchanges (Hyperliquid) - they don't have traditional orderbook metrics
  const supportsOrderbook = exchangeId ? !isDexId(exchangeId) && !isEvmBasedExchange(exchangeId) : false;

  const fetchOrderBook = async (): Promise<OrderBookMetrics | null> => {
    if (!exchangeId || !pairSymbol) return null;

    try {
      const orderbookMetrics: OrderBookMetrics = await apiHelper.get(API_ENDPOINT.HUB_TRADE_ORDER_BOOK_METRICS, {
        queryParams: {
          exchangeId,
          pairSymbol,
        },
      });
      return orderbookMetrics;
    } catch (error) {
      Logger.error("Error processing orderbook:", error);
      return null;
    }
  };

  const { data: orderBookMetrics, ...rest } = useQuery({
    queryKey: ["orderbook", exchangeId, pairSymbol],
    queryFn: fetchOrderBook,
    refetchInterval: refreshInterval,
    staleTime: refreshInterval / 2,
    enabled: enabled && !!exchangeId && !!pairSymbol && supportsOrderbook,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    orderBookMetrics: orderBookMetrics ?? null,
    ...rest,
  };
};
